// ============================================
// Convergence Tracking Service
//
// Tracks model vs doctor correctness over time.
// Two-directional bias detection:
//   - model_correct > 95%: rubber-stamp risk (Dr. C auto-approving)
//   - model_correct < 5%:  anchoring bias (Dr. C never admitting AI was right)
//
// Collection: calibration_convergence/{recordId}
// ============================================

import {
  collection, doc, getDocs, setDoc,
  query, orderBy,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../firebase';
import type { ConvergenceRecord, DrCReview } from '../../types/labUpload';
import { C_CALIBRATION_CONVERGENCE } from '../../constants/collections';

// ── Collection ref ──

const convergenceCol = () => collection(db, C_CALIBRATION_CONVERGENCE);

// ── Core Functions ──

/**
 * Record a convergence data point after Dr. C reviews a case.
 */
export async function recordConvergence(
  caseId: string,
  review: DrCReview,
  alignmentScore: number,
  divergenceAreas: string[],
): Promise<void> {
  const recordId = `conv-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;

  // Determine correctness from review rating
  const modelCorrect = review.rating === 'yes' || review.rating === 'mostly';
  const drCorrect = true; // Dr. C is the ground truth
  const bothCorrect = modelCorrect; // Both correct when model agrees with Dr. C

  const record: ConvergenceRecord = {
    recordId,
    caseId,
    modelCorrect,
    drCorrect,
    bothCorrect,
    divergenceAreas,
    alignmentScore,
    recordedAt: new Date().toISOString(),
  };

  await setDoc(doc(convergenceCol(), recordId), {
    ...record,
    _updatedAt: Timestamp.now(),
  });
}

/**
 * Get convergence statistics across all recorded cases.
 */
export async function getConvergenceStats(): Promise<{
  total: number;
  modelCorrect: number;
  drCorrect: number;
  bothCorrect: number;
  avgAlignment: number;
  modelCorrectRate: number;
  trendByBatch: Array<{ batchStart: number; batchEnd: number; avgAlignment: number }>;
  biasWarning: string | null;
}> {
  const snap = await getDocs(
    query(convergenceCol(), orderBy('recordedAt', 'asc')),
  );

  const records = snap.docs.map((d) => d.data() as ConvergenceRecord);

  if (records.length === 0) {
    return {
      total: 0, modelCorrect: 0, drCorrect: 0, bothCorrect: 0,
      avgAlignment: 0, modelCorrectRate: 0, trendByBatch: [], biasWarning: null,
    };
  }

  let modelCorrect = 0;
  let drCorrect = 0;
  let bothCorrect = 0;
  let totalAlignment = 0;

  for (const r of records) {
    if (r.modelCorrect) modelCorrect++;
    if (r.drCorrect) drCorrect++;
    if (r.bothCorrect) bothCorrect++;
    totalAlignment += r.alignmentScore;
  }

  const modelCorrectRate = records.length > 0 ? modelCorrect / records.length : 0;

  // Compute trend in batches of 25
  const batchSize = 25;
  const trendByBatch: Array<{ batchStart: number; batchEnd: number; avgAlignment: number }> = [];

  for (let i = 0; i < records.length; i += batchSize) {
    const batch = records.slice(i, i + batchSize);
    const batchAvg = batch.reduce((sum, r) => sum + r.alignmentScore, 0) / batch.length;
    trendByBatch.push({
      batchStart: i + 1,
      batchEnd: Math.min(i + batchSize, records.length),
      avgAlignment: Math.round(batchAvg),
    });
  }

  // Two-directional bias detection (both require ≥25 cases for statistical meaning)
  const biasWarnings: string[] = [];

  if (modelCorrectRate > 0.95 && records.length >= 25) {
    biasWarnings.push(
      `Model marked correct ${Math.round(modelCorrectRate * 100)}% of the time. Rubber-stamp risk — verify Dr. C reviews are genuine, not auto-approved.`,
    );
  }

  if (modelCorrectRate < 0.05 && records.length >= 25) {
    biasWarnings.push(
      `Model marked correct only ${Math.round(modelCorrectRate * 100)}% of the time. Anchoring bias risk — Dr. C may be defaulting to his own historical notes and never conceding the AI assessment was right.`,
    );
  }

  const biasWarning = biasWarnings.length > 0 ? biasWarnings.join(' | ') : null;

  return {
    total: records.length,
    modelCorrect,
    drCorrect,
    bothCorrect,
    avgAlignment: Math.round(totalAlignment / records.length),
    modelCorrectRate: Math.round(modelCorrectRate * 100) / 100,
    trendByBatch,
    biasWarning,
  };
}

/**
 * Check if it's time to generate a divergence summary (every 25 reviews).
 */
export async function shouldGenerateDivergenceSummary(): Promise<boolean> {
  const snap = await getDocs(convergenceCol());
  const total = snap.size;
  // Generate summary at 25, 50, 75, 100, etc.
  return total > 0 && total % 25 === 0;
}
